import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, ArrowLeft } from "lucide-react";
import "./TeamSocialPersonalityPage.css";
import progressIcon from "../../../assets/progressIcon5.png";
import progressMobileIconStep1 from "../../../assets/progressIcon2.png";
import progressIconBlue from "../../../assets/progressIcon8.png";
import option1Icon from "../../../assets/girlAndDog03.png";
import option2Icon from "../../../assets/girlAndDog2.png";
import nextIcon from "../../../assets/nextButtonIcon.png";
import nextIcon2 from "../../../assets/nextButtonIcon2.png";
import morningIcon from "../../../assets/availability1.png";
import afternoonIcon from "../../../assets/availability2.png";
import eveningIcon from "../../../assets/availability3.png";
import weekedaysIcon from "../../../assets/availability4.png";
import weekenedsIcon from "../../../assets/availability5.png";
import vacationsIcon from "../../../assets/availability6.png";
import socialPawsLogo from "../../../assets/socialPawsLogo.svg";
import appIcon from "../../../assets/appIcon.svg";
import MobileTopBar from "../../../components/MobileTopBar";
import MobileBottomBar from "../../../components/MobileBottomBar";
import DesktopTopBar from "../../../components/DesktopTopBar";
import DesktopBottomBar from "../../../components/DesktopBottomBar";

const TeamSocialPersonalityPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileStep, setMobileStep] = useState(location.state?.step || 1);

  const [availability, setAvailability] = useState(formData.availability || []);
  const dogActivity =
    formData.dogActivity || formData.dog_activity || formData.activity || "";

  useEffect(() => {
    if (formData.availability && Array.isArray(formData.availability)) {
      setAvailability(formData.availability);
    }
    const act = formData.dogActivity || formData.dog_activity;
    if (act && !formData.dogActivity) {
      setFormData((prev) => ({ ...prev, dogActivity: act.toLowerCase() }));
    }
  }, [formData.availability, formData.dogActivity, formData.dog_activity]);

  // Helpers
  const updateExperience = (value) => {
    setFormData((prev) => ({ ...prev, dogActivity: value }));
  };

  const availabilityOptions = [
    { id: "mornings", label: "Mornings", icon: morningIcon, large: false },
    {
      id: "afternoons",
      label: "Afternoons",
      icon: afternoonIcon,
      large: false,
    },
    { id: "evenings", label: "Evenings", icon: eveningIcon, large: false },
    { id: "weekdays", label: "Weekdays", icon: weekedaysIcon, large: true },
    { id: "weekends", label: "Weekends", icon: weekenedsIcon, large: true },
    { id: "vacations", label: "Vacations", icon: vacationsIcon, large: false },
  ];

  const toggleAvailability = (id) => {
    let updated;
    if (availability.includes(id)) {
      updated = availability.filter((item) => item !== id);
    } else {
      updated = [...availability, id];
    }
    setAvailability(updated);
    setFormData((prev) => ({ ...prev, availability: updated }));
  };

  // Validation
  const isStep1Valid = dogActivity !== "";
  const isStep2Valid = availability.length > 0;
  const isFinalNextEnabled = isStep1Valid && isStep2Valid;

  const handleNext = () => {
    if (window.innerWidth >= 1024) {
      if (!isFinalNextEnabled) return;
      setFormData((prev) => ({
        ...prev,
        availability: availability,
        dogActivity: dogActivity,
      }));
      navigate("/team-social-experience");
    } else {
      if (mobileStep === 1) {
        if (!isStep1Valid) return;
        setFormData((prev) => ({
          ...prev,
          dogActivity: dogActivity,
        }));
        navigate("/team-social-experience");
      } else {
        if (!isStep2Valid) return;
        setFormData((prev) => ({
          ...prev,
          availability: availability,
        }));
        navigate("/team-social-checklist", { state: { step: 2 } });
      }
    }
  };

  const handleSkip = () => {
    if (window.innerWidth >= 1024) {
      navigate("/team-social-experience");
    } else {
      if (mobileStep === 2) {
        navigate("/team-social-checklist", { state: { step: 2 } });
      } else {
        navigate("/team-social-experience");
      }
    }
  };

  const handleBackToDetails = () => {
    switch (formData.dogPersonalityOrigin) {
      case "half-onboarding":
        navigate("/half-onboarding-page");
        break;

      case "half-completed":
        navigate("/half-completed-page");
        break;

      default:
        navigate(-1);
    }
  };

  const handleMobileBack = () => {
    if (mobileStep === 1) {
      navigate("/team-selection");
    } else {
      navigate("/team-social-checklist", { state: { step: 1 } });
    }
  };

  return (
    <div
      className={`tsp-container ${mobileStep === 1 ? "tsp-mobile-step-1" : "tsp-mobile-step-2"}`}
    >
      {/* LEFT PANEL: Experience Selection (Mobile Step 1) */}
      <div
        className={`tsp-panel tsp-left-panel ${mobileStep === 1 ? "tsp-mobile-active" : "tsp-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={handleMobileBack}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
          isProtectedRoute={false}
        />

        <DesktopTopBar
          showBack={true}
          onBack={handleBackToDetails}
          backColor="#000"
          showProgress={true}
          progressIconSrc={progressIcon}
          progressPercent={60}
          iconLeft={52}
          iconWidth="80px"
        />

        <div className="tsp-content-wrapper">
          {/* Header & Progress */}
          <div className="tsp-header-section tsp-mobile-only">
            <div className="tsp-dog-icon-container-step-1">
              <img
                src={progressMobileIconStep1}
                alt="Progress Step"
                className="tsp-progress-icon"
              />
            </div>
            <div className="tsp-progress-track">
              <div className="tsp-progress-fill tsp-step-1-fill"></div>
            </div>
          </div>

          <h1 className="tsp-title">
            HI {formData.name ? formData.name.toUpperCase() : "{NAME}"} WHAT
            SOCIAL PAWS EXPERIENCE ARE YOU HERE FOR?
          </h1>

          <div className="tsp-options-container">
            {/* Option 1: Exercise */}
            <div className="tsp-option-group">
              <div
                className={`tsp-option-card ${
                  dogActivity === "exercise" ? "selected" : ""
                }`}
                onClick={() => updateExperience("exercise")}
              >
                <div className="tsp-exercise-img">
                  <img
                    src={option1Icon}
                    alt="Girl Playing"
                    className="tsp-img-contain"
                  />
                </div>
                <div className="tsp-card-content">
                  <h3>EXERCISE</h3>
                </div>
              </div>
              <p className="tsp-option-desc">
                If you want an active buddy for walks, jogs, or outdoor
                adventures.
              </p>
            </div>

            {/* Option 2: Companionship */}
            <div className="tsp-option-group">
              <div
                className={`tsp-option-card ${
                  dogActivity === "companionship" ? "selected" : ""
                }`}
                onClick={() => updateExperience("companionship")}
              >
                <div className="tsp-card-content">
                  <h3>COMPANIONSHIP</h3>
                </div>
                <div className="tsp-companion-img">
                  <img
                    src={option2Icon}
                    alt="Girl Walking"
                    className="tsp-img-contain"
                  />
                </div>
              </div>
              <p className="tsp-option-desc">
                If you want a calm pup to hang with, work beside, or enjoy
                downtime with.
              </p>
            </div>

            {/* Option 3: Both */}
            <div className="tsp-option-group">
              <div
                className={`tsp-option-card tsp-full-width ${
                  dogActivity === "both" ? "selected-blue" : ""
                }`}
                onClick={() => updateExperience("both")}
              >
                <div className="tsp-btn-content">
                  {formData.dogActivity === "both" && (
                    <Check
                      size={24}
                      strokeWidth={3}
                      className="tsp-check-white tsp-desktop-check"
                    />
                  )}
                  <span
                    className={`tsp-btn-text ${
                      formData.dogActivity === "both" ? "white-text" : ""
                    }`}
                  >
                    BOTH
                  </span>
                </div>
              </div>
              <p className="tsp-option-desc">If you’d be happy with either.</p>
            </div>
          </div>

          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={isStep1Valid}
            nextIconSrc={nextIcon2}
            variant="blue"
          />
        </div>
      </div>

      {/* RIGHT PANEL: Availability Grid (Mobile Step 2) */}
      <div
        className={`tsp-panel tsp-right-panel ${mobileStep === 2 ? "tsp-mobile-active" : "tsp-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={handleMobileBack}
          backColor="#fff"
          logoColor="#fff"
          isProtectedRoute={false}
        />

        <div className="tsp-content-wrapper tsp-right-content">
          <div
            className="tsp-header-section tsp-mobile-only"
            style={{ marginTop: "-28px" }}
          >
            <div className="tsp-dog-icon-container-step-2">
              <img
                src={progressIconBlue}
                alt="Progress Step"
                className="tsp-progress-icon"
              />
            </div>
            <div className="tsp-progress-track">
              <div className="tsp-progress-fill tsp-step-2-fill"></div>
            </div>
          </div>

          <h2 className="tsp-right-title">WHAT IS YOUR AVAILABILITY?</h2>
          <p className="tsp-subtitle">
            This lets Pup Pawrents know when you might be available to walk or
            play with their dog and helps everyone find more suitable matches.
            You'll be able to update this later with more specific availability.
          </p>

          <div className="tsp-avail-grid">
            {availabilityOptions.map((opt) => (
              <div
                key={opt.id}
                className={`tsp-avail-card ${
                  availability.includes(opt.id) ? "selected" : ""
                }`}
                onClick={() => toggleAvailability(opt.id)}
              >
                <div className="tsp-icon-wrapper">
                  <img
                    src={opt.icon}
                    alt={opt.label}
                    className={opt.large ? "tsp-icon-lg" : "tsp-icon-sm"}
                  />
                </div>
                <span className="tsp-avail-label">{opt.label}</span>
              </div>
            ))}
          </div>

          <DesktopBottomBar
            onNext={handleNext}
            isNextEnabled={isFinalNextEnabled}
            nextIconSrc={nextIcon}
            showSkip={false}
            // onSkip={handleSkip}
            // isSkipBlue={true}
            variant="green"
          />

          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={isStep2Valid}
            nextIconSrc={nextIcon}
            showSkip={true}
            onSkip={handleSkip}
            variant="green"
          />
        </div>
      </div>
    </div>
  );
};

export default TeamSocialPersonalityPage;
